/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { $createLinkNode } from '@lexical/link';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { LexicalComposerContext, useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $createQuoteNode } from '@lexical/rich-text';
import {
  $createTextNode,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  LexicalEditor,
  TextNode
} from 'lexical';
import type { JSX } from 'react';
import { useCallback, useMemo, useState } from 'react';
import * as ReactDOM from 'react-dom';


import { useDialog } from '@cms/providers/DialogProvider';
import { SystemTooltip } from '../../../ui/Tooltip';
import { EditEmbedBlurbDialog } from '../../nodes/CustomBlurbNode';
import { EditEmbedChartDialog } from '../../nodes/CustomChartNode';
import { EditEmbedETFDialog } from '../../nodes/CustomETFNode';
import { EditEmbedNowReadDialog } from '../../nodes/CustomNowReadNode';
import { EditEmbedRoundTableIntervieweeDialog } from '../../nodes/CustomRoundTableIntervieweeNode';
import { EditEmbedSectionInfoDialog } from '../../nodes/CustomSectionInfoNode';
import { InsertImageDialog } from '../ImagesPlugin';
import { InsertRawHTMLDialog } from '../RawHTMLPlugin';
import { InsertSocialUrlDialog } from '../SocialUrlPlugin';
import { InsertTableDialog } from '../TablePlugin';

import { CiViewTable } from 'react-icons/ci';
import { FaChartBar, FaUsers } from 'react-icons/fa';
import { IoShareSocial } from 'react-icons/io5';
import { LiaReadme } from "react-icons/lia";
import { MdFormatListBulleted, MdFormatListNumbered, MdFormatQuote, MdHtml, MdInsertLink, MdOutlineHorizontalRule, MdOutlineImage } from 'react-icons/md';
import { RxSection } from "react-icons/rx";
import { TbArticle, TbReceiptBitcoin } from "react-icons/tb";

class ComponentPickerOption extends MenuOption {
  // What shows up in the editor
  title: string;
  // Icon for display
  icon?: JSX.Element;
  // For extra searching.
  keywords: Array<string>;
  // TBD
  keyboardShortcut?: string;
  // What happens when you select this option?
  onSelect: (queryString: string) => void;

  constructor(
    title: string,
    options: {
      icon?: JSX.Element;
      keywords?: Array<string>;
      keyboardShortcut?: string;
      onSelect: (queryString: string) => void;
      component?: JSX.Element;
    },
  ) {
    super(title);
    this.title = title;
    this.keywords = options.keywords || [];
    this.icon = options.icon;
    this.keyboardShortcut = options.keyboardShortcut;
    this.onSelect = options.onSelect.bind(this);
  }
}

function ComponentPickerMenuItem({
  index,
  isSelected,
  isDisabled = false,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  isDisabled?: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: ComponentPickerOption;
}) {
  let className = 'item';

  if (isSelected) {
    className += ' selected';
  }

  const handleClick = () => {
    if (!isDisabled) {
      onClick();
    }
  }

  return (
    <SystemTooltip
      message={option.title}
    >
      <button
        key={option.key}
        tabIndex={-1}
        className={className}
        ref={option.setRefElement}
        id={'typeahead-item-' + index}
        onMouseEnter={onMouseEnter}
        onClick={handleClick}
      >
        {option.icon}
      </button>
    </SystemTooltip>
  );
}

export function ComponentPickerMenuPlugin(): JSX.Element {
  const { openDialog } = useDialog()
  const [editor, lexicalContextType] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);

  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch('@', {
    minLength: 0,
    allowWhitespace: true,
  });

  const onSelectOption = useCallback((
    selectedOption: ComponentPickerOption,
    nodeToRemove: TextNode | null,
    closeMenu: () => void,
    matchingString: string,
  ) => {
    editor.update(() => {
      nodeToRemove?.remove();
      selectedOption.onSelect(matchingString);
      closeMenu();
    });
  }, [editor]);

  const getBaseOptions = useCallback((
    editor: LexicalEditor
  ) => {
    return [
      new ComponentPickerOption('Table', {
        icon: <CiViewTable size={24} />,
        keywords: ['table', 'grid', 'spreadsheet', 'rows', 'columns'],
        onSelect: () => {
          openDialog({
            component: (
              <InsertTableDialog
                activeEditor={editor}
              />
            ),
            maxWidth: 'sm'
          })
        },
      }),
      new ComponentPickerOption('Numbered List', {
        icon: <MdFormatListNumbered size={24} />,
        keywords: ['numbered list', 'ordered list', 'ol'],
        onSelect: () =>
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
      }),
      new ComponentPickerOption('Bulleted List', {
        icon: <MdFormatListBulleted size={24} />,
        keywords: ['bulleted list', 'unordered list', 'ul'],
        onSelect: () =>
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
      }),
      new ComponentPickerOption('Quote', {
        icon: <MdFormatQuote size={24} />,
        keywords: ['block quote'],
        onSelect: () =>
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection) && selection.isCollapsed()) {
              selection.insertNodes([
                $createQuoteNode()
              ]);
            }
          }),
      }),
      // new ComponentPickerOption('Code', {
      //   icon: <IoMdCode size={24} />,
      //   keywords: ['javascript', 'python', 'js', 'codeblock'],
      //   onSelect: () =>
      //     editor.update(() => {
      //       const selection = $getSelection();

      //       if ($isRangeSelection(selection) && selection.isCollapsed()) {
      //         selection.insertNodes([
      //           $createCodeNode(),
      //           // $createParagraphNode()
      //         ]);
      //       }
      //     }),
      // }),
      new ComponentPickerOption('Divider', {
        icon: <MdOutlineHorizontalRule size={24} />,
        keywords: ['horizontal rule', 'divider', 'hr'],
        onSelect: () =>
          editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined),
      }),
      new ComponentPickerOption('Image', {
        icon: <MdOutlineImage size={24} />,
        keywords: ['image', 'photo', 'picture', 'file'],
        onSelect: () => {
          openDialog({
            component: (
              <InsertImageDialog
                activeEditor={editor}
              />
            ),
          })
        },
      }),
      new ComponentPickerOption('Link', {
        icon: <MdInsertLink size={24} />,
        keywords: ['link', 'url', 'hyperlink'],
        onSelect: () => {
          editor.update(() => {
            const selection = $getSelection();

            if ($isRangeSelection(selection) && selection.isCollapsed()) {
              const node = $createLinkNode('', {
                target: '_blank'
              });
              node.append($createTextNode(' '));

              $insertNodes([node]);
            }
          });
        }
      }),
      new ComponentPickerOption('Embed Raw HTML', {
        icon: <MdHtml size={24} />,
        keywords: ['raw', 'html'],
        onSelect: () => {
          openDialog({
            component: (
              <InsertRawHTMLDialog
                activeEditor={editor}
              />
            ),
            maxWidth: 'sm'
          })
        },
      }),
      new ComponentPickerOption('Embed Social URL', {
        icon: <IoShareSocial size={24} />,
        keywords: ['social', 'url', 'embed'],
        onSelect: () => {
          openDialog({
            component: (
              <InsertSocialUrlDialog
                activeEditor={editor}
              />
            ),
            maxWidth: 'sm'
          })
        },
      }),
      new ComponentPickerOption('Section Info', {
        icon: <RxSection size={24} />,
        keywords: ['section', 'info', 'section info'],
        onSelect: () => {
          openDialog({
            component: (
              <LexicalComposerContext.Provider value={[editor, lexicalContextType]}>
                <EditEmbedSectionInfoDialog
                  config={{
                    type: "custom_embed",
                    subtype: "section-info",
                    embed: {
                      id: "",
                      config: {
                        text: "",
                        title: ""
                      }
                    }
                  }}
                />
              </LexicalComposerContext.Provider>
            ),
          })
        }
      }),
      new ComponentPickerOption('Embed Blurb', {
        icon: <TbArticle size={24} />,
        keywords: ['blurb'],
        onSelect: () => {
          openDialog({
            component: (
              <LexicalComposerContext.Provider value={[editor, lexicalContextType]}>
                <EditEmbedBlurbDialog
                  config={{
                    type: "custom_embed",
                    subtype: "blurb",
                    embed: {
                      id: "",
                      config: {
                        text: "",
                        align: "left"
                      }
                    }
                  }}
                />
              </LexicalComposerContext.Provider>
            ),
          })
        },
      }),
      new ComponentPickerOption('Embed Now Read', {
        icon: <LiaReadme size={24} />,
        keywords: ['now', 'read', 'now read'],
        onSelect: () => {
          openDialog({
            component: (
              <LexicalComposerContext.Provider value={[editor, lexicalContextType]}>
                <EditEmbedNowReadDialog
                  config={{
                    type: "custom_embed",
                    subtype: "now-read",
                    embed: {
                      id: "",
                      config: {
                        url: "",
                        text: ""
                      }
                    }
                  }}
                />
              </LexicalComposerContext.Provider>
            ),
          })
        },
      }),
      new ComponentPickerOption('Embed ETF', {
        icon: <TbReceiptBitcoin size={24} />,
        keywords: ['etf', 'bitcoin', 'ethereum'],
        onSelect: () => {
          openDialog({
            component: (
              <LexicalComposerContext.Provider value={[editor, lexicalContextType]}>
                <EditEmbedETFDialog
                  config={{
                    type: "custom_embed",
                    subtype: "embed-etf",
                    embed: {
                      id: "",
                      config: {
                        etf: "bitcoin"
                      }
                    }
                  }}
                />
              </LexicalComposerContext.Provider>
            ),
          })
        },
      }),
      new ComponentPickerOption('Embed Chart', {
        icon: <FaChartBar size={24} />,
        keywords: ['chart', 'charts'],
        onSelect: () => {
          openDialog({
            component: (
              <LexicalComposerContext.Provider value={[editor, lexicalContextType]}>
                <EditEmbedChartDialog
                  config={{
                    type: "custom_embed",
                    subtype: "embed-chart",
                    embed: {
                      id: "",
                      url: "",
                      config: {
                        chartSource: 'api',
                      }
                    }
                  }}
                />
              </LexicalComposerContext.Provider>
            ),
          })
        },
      }),
      new ComponentPickerOption('Embed RoundTable Interviewees', {
        icon: <FaUsers size={24} />,
        keywords: ['roundtable', 'interviewees'],
        onSelect: () => {
          openDialog({
            component: (
              <LexicalComposerContext.Provider value={[editor, lexicalContextType]}>
                <EditEmbedRoundTableIntervieweeDialog
                  config={{
                    type: "custom_embed",
                    subtype: "roundtable-interviewees",
                    embed: {
                      id: "",
                      config: {
                        interviewees: [{
                          id: 1,
                          name: '',
                          photo: '',
                          description: '',
                        }]
                      }
                    }
                  }}
                />
              </LexicalComposerContext.Provider>
            ),
          })
        },
      }),
    ];
  }, [editor]);

  const options = useMemo(() => {
    const baseOptions = getBaseOptions(editor);

    if (!queryString) {
      return baseOptions;
    }

    const regex = new RegExp(queryString, 'i');

    return baseOptions.filter(
      (option) =>
        regex.test(option.title) ||
        option.keywords.some((keyword) => regex.test(keyword)),
    );
  }, [editor, queryString]);

  return (
    <LexicalTypeaheadMenuPlugin<ComponentPickerOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForTriggerMatch}
      options={options}
      ignoreEntityBoundary={true}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) =>
        anchorElementRef.current && options.length
          ? ReactDOM.createPortal(
            <div className="typeahead-popover">
              <div className='options-container'>
                {options.map((option, i: number) => {
                  return (
                    <ComponentPickerMenuItem
                      index={i}
                      isSelected={selectedIndex === i}
                      onClick={() => {
                        setHighlightedIndex(i);
                        selectOptionAndCleanUp(option);
                      }}
                      onMouseEnter={() => {
                        setHighlightedIndex(i);
                      }}
                      key={option.key}
                      option={option}
                    />
                  )
                })}
              </div>
            </div>,
            anchorElementRef.current,
          )
          : null
      }
    />
  );
}
